import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  DOCUMENT,
} from '@angular/core';
import { LoaderThemeService } from './loader-theme.service';

const SPINNER_PATH_D =
  'M1.8 24C0.805887 24 -0.00702475 24.807 0.0674576 25.7983C0.376514 29.9117 1.74178 33.8871 4.04473 37.3337C6.68188 41.2805 10.4302 44.3566 14.8156 46.1731C19.201 47.9896 24.0266 48.4649 28.6822 47.5388C33.3377 46.6128 37.6141 44.327 40.9706 40.9706C44.327 37.6141 46.6128 33.3377 47.5388 28.6822C48.4649 24.0266 47.9896 19.201 46.1731 14.8156C44.3566 10.4302 41.2805 6.68188 37.3337 4.04473C33.8871 1.74179 29.9117 0.376515 25.7984 0.067458C24.807 -0.00702458 24 0.805887 24 1.8C24 2.79411 24.8075 3.59175 25.7977 3.67935C29.1982 3.98016 32.4793 5.13078 35.3336 7.03802C38.6884 9.2796 41.3031 12.4656 42.8471 16.1933C44.3912 19.9209 44.7952 24.0226 44.008 27.9798C43.2209 31.9371 41.278 35.572 38.425 38.425C35.572 41.278 31.9371 43.2209 27.9798 44.008C24.0226 44.7952 19.9209 44.3912 16.1933 42.8471C12.4656 41.3031 9.2796 38.6884 7.03802 35.3336C5.13078 32.4793 3.98016 29.1982 3.67935 25.7977C3.59175 24.8075 2.79411 24 1.8 24Z';

const LIGHT_GRADIENT =
  'conic-gradient(from 90deg, rgba(21, 97, 211, 1) 0deg, rgba(21, 97, 211, 0) 91.875deg, rgba(21, 97, 211, 1) 360deg)';
const DARK_GRADIENT =
  'conic-gradient(from 90deg, rgba(74, 146, 255, 1) 0deg, rgba(74, 146, 255, 0) 91.875deg, rgba(74, 146, 255, 1) 360deg)';

const MIN_SIZE = 16;
const MAX_SIZE = 96;

export type { LoaderTheme } from './loader-theme.service';

@Component({
  selector: 'joii-loader',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './loader.component.html',
  styleUrl: './loader.component.css',
  providers: [LoaderThemeService],
  host: {
    role: 'status',
    '[attr.aria-label]': 'ariaLabel()',
  },
})
export class LoaderComponent {
  private static idCounter = 0;
  private readonly instanceId = LoaderComponent.idCounter++;

  private readonly elementRef = inject(ElementRef);
  private readonly loaderTheme = inject(LoaderThemeService);
  private readonly document = inject(DOCUMENT);

  readonly clipPathId = `joii-loader-clip-${this.instanceId}`;
  readonly clipPathUrl = `url(#${this.clipPathId})`;
  readonly spinnerPathD = SPINNER_PATH_D;

  readonly size = input<number>(48);
  readonly duration = input<number>(800);
  readonly ariaLabel = input<string>('Loading');

  readonly theme = this.loaderTheme.theme;

  readonly effectiveSize = computed(() => {
    const s = this.size();
    return Math.min(MAX_SIZE, Math.max(MIN_SIZE, s));
  });

  readonly spinnerGradient = computed(() => (this.theme() === 'dark' ? DARK_GRADIENT : LIGHT_GRADIENT));

  constructor() {
    this.loaderTheme.observe(this.document.documentElement);

    afterNextRender(() => {
      const host = this.elementRef.nativeElement as HTMLElement;
      const themeEl = host.closest?.('[data-theme]') ?? this.document.documentElement;
      this.loaderTheme.observe(themeEl);
    });
  }
}
